import { ReactNode, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useOverlayLayer } from '../../lib/overlayStack';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    children: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    className?: string;
    /** Prevent closing by clicking backdrop */
    persistent?: boolean;
    /** id of the element that describes the dialog (wired to aria-describedby) */
    ariaDescribedBy?: string;
}

const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
};

/**
 * Modal — Standardised dialog with strict header/body/footer slot structure.
 * Handles backdrop click and Escape key, and labels the dialog with its title.
 * It does not trap focus yet.
 *
 * @example
 * <Modal open={isOpen} onClose={close} title="Create Assessment" size="lg">
 *   <AssessmentForm />
 * </Modal>
 */
export const Modal = ({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'md',
    className,
    persistent = false,
    ariaDescribedBy,
}: ModalProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();

    // Escape-to-close and the body scroll lock are shared across every overlay,
    // so a modal with another overlay open above it doesn't close on that
    // overlay's Escape, and that overlay closing doesn't unlock scroll behind us.
    useOverlayLayer({
        active: open,
        onEscape: persistent ? undefined : onClose,
    });

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[20000] flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={ariaDescribedBy}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm touch-none"
                onClick={() => !persistent && onClose()}
                aria-hidden
            />

            {/* Panel */}
            <div
                ref={panelRef}
                className={cn(
                    'relative z-10 w-full bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl shadow-black/10 rounded-3xl overflow-hidden',
                    'animate-in fade-in zoom-in-95 duration-200',
                    'flex flex-col max-h-[90vh]',
                    sizeClasses[size],
                    className
                )}
            >
                {/* Header */}
                {(title || description) && (
                    <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 shrink-0">
                        <div>
                            {title && <h2 id={titleId} className="text-lg font-black text-slate-900 tracking-tight">{title}</h2>}
                            {description && <p className="text-sm text-slate-500 mt-0.5 font-medium">{description}</p>}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close modal"
                            className="ml-4 p-1.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal-primary/20"
                        >
                            <X size={18} />
                        </button>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5">{children}</div>

                {/* Footer */}
                {footer && (
                    <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0 flex justify-end gap-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};
